import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useSearchParams } from "react-router";
import {
  IKindredChangeLogFilter,
  KindredChangeEntityType,
  KindredChangeSource,
} from "../../interfaces";
import { FilterBar, FilterField, PageHeader } from "../../components/ui";
import {
  CHANGE_ENTITY_LABEL,
  CHANGE_SOURCE_LABEL,
  ChangeLogTable,
} from "../../components/workbench";

const FIELDS = ["entityType", "source", "actor", "domain", "brandId", "entityId", "dateFrom", "dateTo"] as const;
type Field = (typeof FIELDS)[number];

export const KindredChangeLog: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = Object.fromEntries(
    FIELDS.map((f) => [f, searchParams.get(f) ?? ""])
  ) as Record<Field, string>;
  const [form, setForm] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [actors, setActors] = useState<string[]>([]);

  const set = (f: Field) => (e: React.ChangeEvent<any>) =>
    setForm((prev) => ({ ...prev, [f]: e.target.value }));

  const apply = (next: Record<Field, string>) => {
    setApplied(next);
    setSearchParams(
      Object.fromEntries(Object.entries(next).filter(([, v]) => v)),
      { replace: true }
    );
  };

  const toFilter = (v: Record<Field, string>): IKindredChangeLogFilter => ({
    entityType: (v.entityType || undefined) as KindredChangeEntityType | undefined,
    source: (v.source || undefined) as KindredChangeSource | undefined,
    actor: v.actor || undefined,
    domain: v.domain || undefined,
    brandId: v.brandId || undefined,
    entityId: v.entityId || undefined,
    dateFrom: v.dateFrom ? new Date(`${v.dateFrom}T00:00:00`).toISOString() : undefined,
    dateTo: v.dateTo ? new Date(`${v.dateTo}T23:59:59`).toISOString() : undefined,
  });

  const activeCount = Object.values(applied).filter(Boolean).length;
  const empty = Object.fromEntries(FIELDS.map((f) => [f, ""])) as Record<Field, string>;

  return (
    <div>
      <PageHeader
        title="Kindred Change Log"
        description="Every change this app makes to Kindred deals, coupons, offers, brands, brand links, the brand registry and targeting: who, when, why, and the before/after of each field."
      />
      <FilterBar
        onSearch={() => apply(form)}
        onClear={() => {
          setForm(empty);
          apply(empty);
        }}
        activeCount={activeCount}
      >
        <FilterField label="Row type" md={3}>
          <Form.Select size="sm" value={form.entityType} onChange={set("entityType")}>
            <option value="">All</option>
            {Object.entries(CHANGE_ENTITY_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField label="Made from" hint="Which part of the app made the change." md={3}>
          <Form.Select size="sm" value={form.source} onChange={set("source")}>
            <option value="">All</option>
            {Object.entries(CHANGE_SOURCE_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField label="Who" md={3}>
          <Form.Select size="sm" value={form.actor} onChange={set("actor")}>
            <option value="">Anyone</option>
            {actors.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField label="Domain" hint="Bare and www. spellings both match." md={3}>
          <Form.Control size="sm" placeholder="example.com" value={form.domain} onChange={set("domain")} />
        </FilterField>
        <FilterField label="Brand ID" md={3}>
          <Form.Control size="sm" value={form.brandId} onChange={set("brandId")} />
        </FilterField>
        <FilterField label="Row ID" hint="A deal, coupon, offer or brand id." md={3}>
          <Form.Control size="sm" value={form.entityId} onChange={set("entityId")} />
        </FilterField>
        <FilterField label="From" md={3}>
          <Form.Control size="sm" type="date" value={form.dateFrom} onChange={set("dateFrom")} />
        </FilterField>
        <FilterField label="To" md={3}>
          <Form.Control size="sm" type="date" value={form.dateTo} onChange={set("dateTo")} />
        </FilterField>
      </FilterBar>

      <ChangeLogTable filter={toFilter(applied)} pageSize={50} onActorOptions={setActors} />
    </div>
  );
};
